import * as api from '@gitbook/api-client';
import { SiteType } from '@gitbook/api-client';
import { ensureIsDefined } from '@gitbook/util-ts';

export type FreeSiteType = SiteType.Basic | SiteType.Sponsored;

export enum SitePlanTarget {
    CUSTOMERS = 'customers',
    OPEN_SOURCE = 'open-source',
}

/**
 * The pricing definition object for a site type
 */
export type SitePricingDefinition = {
    key: SiteType;
    title: string;
    subtitle: string;
    description: string;
    targets: readonly SitePlanTarget[];
    featured?: boolean;
};

/**
 * An array of all Site products.
 *
 * The order matters: products should be sorted from lowest to highest.
 *
 * This is because we use indexes to determine whether we are upgrading or
 * downgrading, when switching from an product to another.
 */
export const SITE_PRICING_PRODUCTS: readonly SitePricingDefinition[] = [
    {
        key: SiteType.Basic,
        title: 'Basic',
        subtitle: 'Create a personal docs site for free',
        description: 'Public-only docs site with basic customization and features.',
        targets: [SitePlanTarget.OPEN_SOURCE, SitePlanTarget.CUSTOMERS],
    },
    {
        key: SiteType.Sponsored,
        title: 'Sponsored',
        subtitle: 'Incredible open source docs that fund your project',
        description: 'Public-only docs site to fund your open source project.',
        targets: [SitePlanTarget.OPEN_SOURCE],
    },
    {
        key: SiteType.Premium,
        title: 'Essential',
        subtitle: 'Build fully-branded, AI-powered documentation',
        description:
            'Docs site with custom domain, advanced customization and additional sharing options.',
        targets: [SitePlanTarget.CUSTOMERS],
    },
    {
        key: SiteType.Ultimate,
        title: 'Ultimate',
        subtitle: 'Centralize your docs with granular access control',
        description:
            'Docs site with sections, premium features, and full audience controls including authenticated access.',
        targets: [SitePlanTarget.CUSTOMERS],
    },
] as const;

export function isFreeSiteType(siteType: SiteType): siteType is FreeSiteType {
    return siteType === SiteType.Basic || siteType === SiteType.Sponsored;
}

export function compareSiteTypes(a: SiteType, b: SiteType): number {
    const ALL_SITE_TYPES: SiteType[] = SITE_PRICING_PRODUCTS.map((product) => product.key);

    return ALL_SITE_TYPES.indexOf(a) - ALL_SITE_TYPES.indexOf(b);
}

export function isSiteTypeDowngrade(from: SiteType, to: SiteType): boolean {
    return compareSiteTypes(from, to) > 0;
}

/**
 * Get the site product definition for a given site type.
 */
export function getSiteProductDefinition(siteType: SiteType): SitePricingDefinition {
    const definition = SITE_PRICING_PRODUCTS.find((product) => product.key === siteType);
    return ensureIsDefined(definition, `Site type ${siteType} not found in pricing definitions`);
}

/**
 * Determines whether the given site type is paid.
 */
export function checkIsPaidSiteProductKey(
    key: string
): key is api.SiteType.Ultimate | api.SiteType.Premium {
    return key === api.SiteType.Premium || key === api.SiteType.Ultimate;
}
